/**
 * Project Settings Page
 * @implements US-015 - Project Editing (Frontend)
 * @implements FS-002.2.1 - Edit Project Details
 * @implements BS-002.3 - Project Title Uniqueness
 * @implements BS-002.4 - Description Length Limit
 *
 * Allows Product Owners to edit project title and description.
 * Handles archived/pending deletion projects by disabling the form.
 *
 * Route: /projects/:id/settings
 */

import { useState, useEffect, FormEvent } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useQuery, useMutation, gql } from '@apollo/client';
import {
  LifecycleStateBadge,
  LifecycleState,
  ControlState,
} from '@/components/project';

/**
 * Maximum description length
 * @implements BS-002.4 - Description Length Limit
 */
const MAX_DESCRIPTION_LENGTH = 256;

/**
 * GraphQL query for fetching a single project
 * @implements FS-002.3.2 - Project Navigation
 */
const GET_PROJECT_QUERY = gql`
  query GetProject($id: ID!) {
    project(id: $id) {
      id
      title
      description
      lifecycleState
      controlState
      createdAt
      updatedAt
    }
  }
`;

/**
 * GraphQL mutation for updating a project
 * @implements FS-002.2.1 - Edit Project Details
 * @implements US-015 - Project Editing
 */
const UPDATE_PROJECT_MUTATION = gql`
  mutation UpdateProject($id: ID!, $input: UpdateProjectInput!) {
    updateProject(id: $id, input: $input) {
      id
      title
      description
      lifecycleState
      controlState
      updatedAt
    }
  }
`;

/**
 * Project type from the GraphQL response
 */
interface Project {
  id: string;
  title: string;
  description: string | null;
  lifecycleState: LifecycleState;
  controlState: ControlState;
  createdAt: string;
  updatedAt: string;
}

/**
 * Response type for GetProject query
 */
interface GetProjectResponse {
  project: Project | null;
}

/**
 * Variables for GetProject query
 */
interface GetProjectVariables {
  id: string;
}

/**
 * Input type for UpdateProject mutation
 */
interface UpdateProjectInput {
  title?: string;
  description?: string | null;
}

/**
 * Response type for UpdateProject mutation
 */
interface UpdateProjectResponse {
  updateProject: {
    id: string;
    title: string;
    description: string | null;
    lifecycleState: LifecycleState;
    controlState: ControlState;
    updatedAt: string;
  };
}

/**
 * Extract error code from GraphQL error
 * @implements BS-002.3 - Project Title Uniqueness (DUPLICATE_TITLE error)
 * @implements FS-002.2.1 - Error handling for PROJECT_ARCHIVED and PROJECT_NOT_FOUND
 */
function getErrorMessage(error: Error): {
  message: string;
  isNotFound: boolean;
} {
  const graphQLError = error as {
    graphQLErrors?: Array<{
      extensions?: { code?: string };
      message?: string;
    }>;
  };

  if (graphQLError.graphQLErrors?.length) {
    const firstError = graphQLError.graphQLErrors[0];
    const code = firstError.extensions?.code;

    switch (code) {
      case 'DUPLICATE_TITLE':
        return {
          message: 'A project with this title already exists',
          isNotFound: false,
        };
      case 'PROJECT_ARCHIVED':
        return {
          message: 'Cannot edit archived project',
          isNotFound: false,
        };
      case 'PROJECT_NOT_FOUND':
        return {
          message: 'Project not found',
          isNotFound: true,
        };
      default:
        break;
    }
  }

  return {
    message: error.message || 'Failed to update project. Please try again.',
    isNotFound: false,
  };
}

/**
 * ProjectSettingsPage - Edit project settings
 *
 * @implements US-015 - Project Editing (Frontend)
 * @implements FS-002.2.1 - Edit Project Details
 *
 * Features:
 * - Fetches current project data
 * - Pre-populates form with current values
 * - Validates title (required) and description (max 256 chars)
 * - Handles archived/pending deletion by disabling form
 * - Shows success snackbar on save
 * - Handles errors: DUPLICATE_TITLE, PROJECT_ARCHIVED, PROJECT_NOT_FOUND
 *
 * Route: /projects/:id/settings
 */
export const ProjectSettingsPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [titleError, setTitleError] = useState<string | null>(null);
  const [successOpen, setSuccessOpen] = useState(false);

  // Fetch project data
  const {
    data,
    loading: queryLoading,
    error: queryError,
  } = useQuery<GetProjectResponse, GetProjectVariables>(GET_PROJECT_QUERY, {
    variables: { id: id! },
    skip: !id,
    fetchPolicy: 'cache-and-network',
  });

  // Update mutation
  const [updateProject, { loading: mutationLoading, error: mutationError }] =
    useMutation<
      UpdateProjectResponse,
      { id: string; input: UpdateProjectInput }
    >(UPDATE_PROJECT_MUTATION);

  const project = data?.project;

  // Populate form when project data loads
  useEffect(() => {
    if (project) {
      setTitle(project.title);
      setDescription(project.description || '');
    }
  }, [project]);

  // Check if project is editable
  const isArchived = project?.lifecycleState === 'ARCHIVED';
  const isPendingDeletion = project?.lifecycleState === 'PENDING_DELETION';
  const isDisabled = isArchived || isPendingDeletion || mutationLoading;

  /**
   * Handle title change
   */
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
    if (titleError) {
      setTitleError(null);
    }
  };

  /**
   * Handle description change with character limit
   * @implements BS-002.4 - Description Length Limit
   */
  const handleDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const value = e.target.value;
    if (value.length <= MAX_DESCRIPTION_LENGTH) {
      setDescription(value);
    }
  };

  /**
   * Handle form submission
   * @implements FS-002.2.1 - Edit Project Details
   */
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTitleError(null);

    // Validate title
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setTitleError('Project title is required');
      return;
    }

    try {
      const result = await updateProject({
        variables: {
          id: id!,
          input: {
            title: trimmedTitle,
            description: description.trim() || null,
          },
        },
      });

      if (result.data?.updateProject) {
        setSuccessOpen(true);
      }
    } catch (err) {
      // Error handling is done via the error state from useMutation
      console.error('Failed to update project:', err);

      // Handle PROJECT_NOT_FOUND by redirecting
      const error = err as Error;
      const { isNotFound } = getErrorMessage(error);
      if (isNotFound) {
        navigate('/projects');
      }
    }
  };

  /**
   * Handle snackbar close
   */
  const handleSnackbarClose = () => {
    setSuccessOpen(false);
  };

  // Get display error message
  const displayError = mutationError
    ? getErrorMessage(mutationError).message
    : null;

  // Render loading state
  if (queryLoading && !data) {
    return (
      <Container maxWidth="md">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Render error state
  if (queryError) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load project: {queryError.message}
          </Alert>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/projects')}
          >
            Back to Projects
          </Button>
        </Box>
      </Container>
    );
  }

  // Render 404 state (project not found)
  if (!project) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: 'center',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="h5" gutterBottom>
              Project Not Found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              The project you are looking for does not exist or you do not have
              access to it.
            </Typography>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/projects')}
            >
              Back to Projects
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 3 }}
        >
          <Link
            component={RouterLink}
            to="/projects"
            color="inherit"
            underline="hover"
          >
            Projects
          </Link>
          <Link
            component={RouterLink}
            to={`/projects/${project.id}`}
            color="inherit"
            underline="hover"
          >
            {project.title}
          </Link>
          <Typography color="text.primary">Settings</Typography>
        </Breadcrumbs>

        {/* Page Title */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3,
          }}
        >
          <Typography variant="h4" component="h1">
            Project Settings
          </Typography>
          <LifecycleStateBadge state={project.lifecycleState} />
        </Box>

        {/* Archived/Pending Deletion Alert */}
        {isArchived && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            This project is archived and cannot be edited. Unarchive to make
            changes.
          </Alert>
        )}
        {isPendingDeletion && (
          <Alert severity="error" sx={{ mb: 3 }}>
            This project is pending deletion and cannot be edited.
          </Alert>
        )}

        {/* Settings Form */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box component="form" onSubmit={handleSubmit}>
            {/* Title Field (required) */}
            <TextField
              fullWidth
              label="Project Title"
              value={title}
              onChange={handleTitleChange}
              required
              error={!!titleError}
              helperText={titleError || 'The name of your project'}
              disabled={isDisabled}
              sx={{ mb: 3 }}
              inputProps={{
                'aria-describedby': 'title-helper-text',
              }}
            />

            {/* Description Field (optional with counter) */}
            <TextField
              fullWidth
              label="Description"
              value={description}
              onChange={handleDescriptionChange}
              multiline
              rows={4}
              placeholder="Optional: Describe what this project is about"
              helperText={`${description.length}/${MAX_DESCRIPTION_LENGTH} characters`}
              disabled={isDisabled}
              sx={{ mb: 3 }}
              inputProps={{
                'aria-describedby': 'description-helper-text',
                maxLength: MAX_DESCRIPTION_LENGTH,
              }}
            />

            {/* Error Alert */}
            {displayError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {displayError}
              </Alert>
            )}

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => navigate(`/projects/${project.id}`)}
              >
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={isDisabled}>
                {mutationLoading ? (
                  <>
                    <CircularProgress
                      size={20}
                      color="inherit"
                      sx={{ mr: 1 }}
                    />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Back to Project Link */}
        <Box sx={{ mt: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/projects/${project.id}`)}
            color="primary"
          >
            Back to Project
          </Button>
        </Box>
      </Box>

      {/* Success Snackbar */}
      <Snackbar
        open={successOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        message="Project updated successfully"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Container>
  );
};
